import React from 'react'
import { motion } from 'framer-motion'
import { Trophy, Users } from 'lucide-react'
import GlassPanel from '../components/GlassPanel'
import Button from '../components/Button'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'

const FinalScorePage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate()
  const location = useLocation()
  const { team1Name, team2Name, team1Score, team2Score } = location.state || {}

  const winner =
    team1Score > team2Score
      ? team1Name
      : team2Score > team1Score
      ? team2Name
      : null
  const isTie = team1Score === team2Score

  return (
    <div
      className={`relative h-screen w-screen overflow-hidden flex flex-col items-center justify-center p-4 md:p-8 ${
        theme === 'dark' 
          ? 'bg-gradient-to-br from-[#2D3436] to-[#0D0F1A]' 
          : 'bg-gradient-to-br from-[#F7F9FB] to-[#D6E4FF]'
      }`}
    >
      {/* Floating Shapes */}
      <motion.div
        className={`absolute top-10 left-[15%] w-16 h-16 rounded-full z-0 ${
          theme === 'dark' ? 'bg-[#4B7BFF]/20' : 'bg-[#4B7BFF]/20'
        }`}
        animate={{ y: [0, 25, 0], x: [0, -15, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className={`absolute top-[55%] right-[10%] w-24 h-24 rounded-full z-0 ${
          theme === 'dark' ? 'bg-[#8E44AD]/20' : 'bg-[#8E44AD]/20'
        }`}
        animate={{ y: [0, -20, 0], x: [0, 20, 0] }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1,
        }}
      />

      {/* Main Container */}
      <motion.div
        className="relative z-10 w-full max-w-2xl mx-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <GlassPanel variant="elevated" className="w-full p-8">
          {/* Title */}
          <h1 className={`text-4xl font-bold text-center mb-8 ${
            theme === 'dark' ? 'text-[#4B7BFF]' : 'text-[#4B7BFF]'
          }`}>
            {isTie ? 'تعادل!' : `${winner} هو الفائز!`}
          </h1>

          {/* Scoreboard */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            {/* Team 1 */}
            <div className="text-center">
              <h2 className={`text-2xl font-bold mb-2 ${
                theme === 'dark' ? 'text-white' : 'text-[#2D3748]'
              }`}>
                {team1Name || 'الفريق الأول'}
              </h2>
              <div className="flex items-center justify-center gap-2">
                <Trophy className="text-[#FDCB6E]" />
                <span className={`text-3xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-[#2D3748]'
                }`}>
                  {team1Score || 0}
                </span>
              </div>
            </div>
            {/* Team 2 */}
            <div className="text-center">
              <h2 className={`text-2xl font-bold mb-2 ${
                theme === 'dark' ? 'text-white' : 'text-[#2D3748]'
              }`}>
                {team2Name || 'الفريق الثاني'}
              </h2>
              <div className="flex items-center justify-center gap-2">
                <Trophy className="text-[#FDCB6E]" />
                <span className={`text-3xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-[#2D3748]'
                }`}>
                  {team2Score || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Winner / Tie Message */}
          {!isTie && winner ? (
            <motion.div
              className="flex flex-col items-center mb-8"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            >
              <Users className="text-[#6C5CE7] mb-2" size={48} />
              <span className={`text-xl font-semibold text-[#FDCB6E] ${
                theme === 'dark' ? 'bg-black/30' : 'bg-[#2D3748]/10'
              } px-4 py-2 rounded-lg`}>
                تهانينا لـ {winner}!
              </span>
            </motion.div>
          ) : isTie ? (
            <motion.div
              className="flex flex-col items-center mb-8"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            >
              <Users className="text-[#6C5CE7] mb-2" size={48} />
              <span className={`text-xl font-semibold text-[#FDCB6E] ${
                theme === 'dark' ? 'bg-black/30' : 'bg-[#2D3748]/10'
              } px-4 py-2 rounded-lg`}>
                تعادل بين الفريقين!
              </span>
            </motion.div>
          ) : null}

          {/* Replay Button */}
          <div className="flex justify-center">
            <Button
              variant="primary"
              className="px-6 py-3 text-lg"
              onClick={() => navigate('/')}
            >
              العب مرة أخرى
            </Button>
          </div>
        </GlassPanel>
      </motion.div>
    </div>
  )
}

export default FinalScorePage
